import React, { useRef, useState } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';

import { useTranslation } from '@modernloop/i18n';
import { ArrowRightIcon, CookieIcon, HelpIcon, LogoutIcon, SettingsIcon, UserEditIcon } from '@modernloop/shared/icons';
import {
  Avatar,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import ImageBlock from 'src/components/ImageBlock';
import HelpCenterUrl from 'src/constants/HelpCenterUrl';
import { useIsMLoopInternalOnly } from 'src/hooks/feature-flag/useIsMLoopInternalOnly';
import useAuth from 'src/hooks/useAuth';
import useHistory from 'src/hooks/useHistory';
import useOrgName from 'src/hooks/useOrgName';

import { AccountSettingsTab, getAccountSettingUrls } from 'src/urls/getAccountSettingUrls';
import { OrganizationSettingsTab, getOrganizationSettingUrls } from 'src/urls/getOrganizationSettingUrls';

import AccountMenuDarkModeItem from './AccountMenuDarkModeItem';
import AccountMenuDeveloperItems from './AccountMenuDeveloperItems';
import AccountMenuLanguageItem from './AccountMenuLanguageItem';
import AccountMenuSwitchTenantItem from './AccountMenuSwitchTenantItem';

type Props = {
  expanded: boolean;
  size?: 'small' | 'medium';
};

const AccountMenu = ({ expanded, size = 'medium' }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { userInfo, logout } = useAuth();
  const { orgName } = useOrgName();
  const { pathname } = useLocation();
  const isMLoopInternalOnly = useIsMLoopInternalOnly();

  const [showMenu, setShowMenu] = useState(false);
  const avatarRef = useRef<HTMLButtonElement>(null);

  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const handleLogout = async (): Promise<void> => {
    try {
      setShowMenu(false);
      logout();
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t('hiring-dashboard-common:common.toast.unableToLogout'), {
        variant: 'error',
      });
    }
  };

  const accountSettingUrl = getAccountSettingUrls({
    tab: AccountSettingsTab.GENERAL,
  });
  const organizationSettingUrl = getOrganizationSettingUrls({
    tab: OrganizationSettingsTab.SCHEDULE_SETTINGS,
    returnTo: pathname,
  });
  const employeeName = userInfo?.employeeName || t('hiring-dashboard-nav:nav.dashboard.yourAccount');

  return (
    <>
      {!expanded && (
        <IconButton
          id="app-nav-account"
          data-testid="app-nav-account"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.background.contrast,
              boxShadow: 'none',
              '& svg': {
                color: (theme) => theme.palette.info.main,
              },
            },
          }}
          onClick={() => setShowMenu(true)}
          onMouseEnter={() => setShowMenu(true)}
          ref={avatarRef}
        >
          <Avatar alt={employeeName} src={userInfo?.employeeImageUrl} />
        </IconButton>
      )}
      {expanded && (
        <Button
          id="app-nav-account"
          data-testid="app-nav-account"
          size={size}
          sx={{
            height: 'auto',
            minHeight: '28px',
            display: 'flex',
            justifyContent: 'flex-start',
            overflow: 'hidden',
            textAlign: 'left',
            paddingLeft: (theme) => theme.spacing(1.5),
            borderRadius: '6px',
            '&:hover': {
              color: (theme) => theme.palette.primary.main,
            },
          }}
          fullWidth
          ref={avatarRef}
          onClick={() => setShowMenu(true)}
          onMouseEnter={() => setShowMenu(true)}
          startIcon={
            <Avatar alt={employeeName} src={userInfo?.employeeImageUrl} sx={{ width: '20px', height: '20px' }} />
          }
          variant="text"
        >
          <Stack alignItems="flex-start" overflow="hidden">
            <Typography variant="body2" fontWeight={500} noWrap width="100%" textAlign="left">
              {employeeName}
            </Typography>
            {orgName && (
              <Typography variant="caption" color="text.secondary" noWrap width="100%" textAlign="left">
                {orgName}
              </Typography>
            )}
          </Stack>
        </Button>
      )}
      {showMenu && (
        <Menu
          anchorEl={avatarRef.current}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          open
          onClose={() => setShowMenu(false)}
          slotProps={{ paper: { sx: { minWidth: 248 } } }}
        >
          <ListSubheader component="div" disableSticky sx={{ padding: 1.5, lineHeight: 'normal' }}>
            <ImageBlock
              image={
                <Avatar alt={employeeName} src={userInfo?.employeeImageUrl} sx={{ width: '36px', height: '36px' }} />
              }
              title={orgName ?? ''}
              caption={
                <>
                  <Typography>{employeeName}</Typography>
                  <Typography color="text.secondary">{userInfo?.employeeEmail}</Typography>
                </>
              }
            />
          </ListSubheader>

          {/*
           * Renders nothing until the user holds a Membership in a second Organization, so the
           * divider below stays the one separator between the account header and the actions.
           */}
          <AccountMenuSwitchTenantItem onSelect={() => setShowMenu(false)} />

          <Divider sx={{ margin: '8px 0' }} />

          <MenuItem
            onClick={() => {
              history.replace(accountSettingUrl);
              setShowMenu(false);
            }}
          >
            <ListItemIcon>
              <UserEditIcon />
            </ListItemIcon>
            <RouterLink
              to={accountSettingUrl}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexGrow: 1 }}
            >
              <Typography color="text.primary">
                {t('hiring-dashboard-nav:nav.dashboard.personalPreferences')}
              </Typography>
            </RouterLink>
          </MenuItem>
          <MenuItem
            onClick={() => {
              history.push('/ip');
              setShowMenu(false);
            }}
          >
            <ListItemIcon>
              <ArrowRightIcon />
            </ListItemIcon>
            <RouterLink to="/ip" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Typography color="text.primary">
                {t('hiring-dashboard-nav:nav.dashboard.goToInterviewerPortal')}
              </Typography>
            </RouterLink>
          </MenuItem>
          <AccountMenuDarkModeItem />
          <AccountMenuLanguageItem onSelect={() => setShowMenu(false)} />

          <Divider sx={{ margin: '8px 0' }} />

          <MenuItem
            onClick={() => {
              history.replace(organizationSettingUrl);
              setShowMenu(false);
            }}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <RouterLink
              to={organizationSettingUrl}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexGrow: 1 }}
            >
              <Typography color="text.primary">{t('hiring-dashboard-nav:nav.organizationSettings')}</Typography>
            </RouterLink>
          </MenuItem>

          <Divider sx={{ margin: '8px 0' }} />

          <MenuItem
            component="a"
            href={HelpCenterUrl.HOME}
            rel="noopener noreferrer"
            target="_blank"
            onClick={() => setShowMenu(false)}
          >
            <ListItemIcon>
              <HelpIcon />
            </ListItemIcon>
            <Typography>{t('hiring-dashboard-nav:nav.helpAndFeedback')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => window?.OneTrust?.ToggleInfoDisplay()}>
            <ListItemIcon>
              <CookieIcon />
            </ListItemIcon>
            <Typography>{t('common:nav.cookieSettings')}</Typography>
          </MenuItem>

          <Divider sx={{ margin: '8px 0' }} />

          {isMLoopInternalOnly && (
            <>
              <AccountMenuDeveloperItems />
              <Divider sx={{ margin: '8px 0' }} />
            </>
          )}

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <Typography color="text.primary">{t('common:nav.logout')}</Typography>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default AccountMenu;
